import { useEffect, useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useThreadStore, type ThreadListItem } from '@/stores/thread.store';
import { useWebSocket } from '@/hooks/useWebSocket';
import { fetchThreads } from '@/lib/api';
import { toast } from '@/stores/toast.store';
import { EmptyState } from '@/components/ui/empty-state';
import type { WSServerEvent } from '@goodchat/shared/types';

// Stable empty array — prevents Zustand selector `?? []` from creating new refs → infinite re-render loop
const EMPTY_THREADS: ThreadListItem[] = [];

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

export function AreaThreadList({
  orgId,
  areaId,
  areaName,
  orgSlug,
  areaSlug,
  onThreadClick,
  selectedThreadId,
}: {
  orgId: string;
  areaId: string;
  areaName: string;
  orgSlug: string;
  areaSlug: string;
  onThreadClick?: (threadId: string) => void;
  selectedThreadId?: string | null;
}) {
  const threads = useThreadStore((s) => s.threadsByArea[areaId] ?? EMPTY_THREADS);
  const { setThreads, addThread, updateThread } = useThreadStore();

  const [statusFilter, setStatusFilter] = useState<'open' | 'resolved' | 'all'>('open');

  useEffect(() => {
    if (!orgId || !areaId) return;
    // Debounce to avoid hammering API on rapid filter clicks
    const timer = setTimeout(() => {
      fetchThreads(orgId, areaId, statusFilter)
        .then((data) => setThreads(areaId, data.threads))
        .catch(() => toast.error('Failed to load threads'));
    }, 150);
    return () => clearTimeout(timer);
  }, [orgId, areaId, statusFilter, setThreads]);

  const wsHandler = useCallback(
    (event: WSServerEvent) => {
      if (event.event === 'thread.new' && event.areaId === areaId) {
        addThread(areaId, event.thread);
      } else if (event.event === 'thread.updated') {
        updateThread(event.threadId, {
          title: event.title,
          isResolved: event.isResolved,
          updatedAt: event.updatedAt,
        });
      }
    },
    [areaId, addThread, updateThread],
  );
  useWebSocket(wsHandler);

  return (
    <div className="flex h-full flex-col">
      {/* Filter + Create */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-800">
        <div className="flex gap-1">
          <button
            onClick={() => setStatusFilter('open')}
            className={`flex items-center gap-1 rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
              statusFilter === 'open'
                ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="9" />
            </svg>
            Open
          </button>
          <button
            onClick={() => setStatusFilter('resolved')}
            className={`flex items-center gap-1 rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
              statusFilter === 'resolved'
                ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Resolved
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`flex items-center gap-1 rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            All
          </button>
        </div>
        <Link
          to="/org/$orgSlug/$areaSlug/threads/new"
          params={{ orgSlug, areaSlug }}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          New Thread
        </Link>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <EmptyState
            icon="thread"
            title="No threads yet"
            description="Start a conversation thread from any message."
            fillHeight
          />
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {threads.map((thread) => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                isSelected={selectedThreadId === thread.id}
                onClick={() => onThreadClick?.(thread.id)}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

function ThreadRow({
  thread,
  isSelected,
  onClick,
}: {
  thread: ThreadListItem;
  isSelected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
        isSelected
          ? 'border-l-2 border-l-blue-500 bg-blue-50 dark:bg-blue-950/30'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
      }`}
    >
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        {thread.author.displayName.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`truncate text-sm font-medium ${thread.unreadCount > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
            {thread.title}
          </span>
          {thread.isResolved && (
            <span className="flex-shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
              Resolved
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>{thread.author.displayName}</span>
          <span>&middot;</span>
          <span>{thread.messageCount} {thread.messageCount === 1 ? 'message' : 'messages'}</span>
          {thread.lastMessageAt && (
            <>
              <span>&middot;</span>
              <span>{formatRelativeTime(thread.lastMessageAt)}</span>
            </>
          )}
        </div>
      </div>
      {thread.unreadCount > 0 && (
        <span className="min-w-[20px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
          {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
        </span>
      )}
    </button>
  );
}
